import React from "react";
import { Card, Col, Row, Tooltip } from "antd";
import {
  AppstoreOutlined,
  TrophyOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import {
  MaterialSummaryData,
  fmtINR,
  fmtINRCompact,
  num,
} from "./materialShared";

// KPI cards, style copied from ThreeMConsolidation/SummaryCards.tsx —
// compact ₹ value with a Tooltip carrying the exact fmtINR amount.

const valueStyle: React.CSSProperties = {
  fontSize: 20,
  fontWeight: 700,
  color: "#1a1a2e",
  marginTop: 6,
  fontVariantNumeric: "tabular-nums",
  cursor: "default",
  width: "fit-content",
};

const titleStyle = (color: string): React.CSSProperties => ({
  fontSize: 12.5,
  fontWeight: 600,
  color,
  display: "flex",
  alignItems: "center",
  gap: 6,
});

const cardStyle = (background: string, border: string): React.CSSProperties => ({
  borderRadius: 12,
  background,
  border: `1px solid ${border}`,
  height: "100%",
});

const subStyle: React.CSSProperties = {
  fontSize: 12,
  color: "#8c8c8c",
  marginTop: 2,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const MaterialKpiCards: React.FC<{
  data?: MaterialSummaryData | null;
  grandTotal: number;
}> = ({ data, grandTotal }) => {
  const materials = data?.materials || [];
  const totalCost = grandTotal;
  const materialCount = num(data?.materialCount) || materials.length;
  // API sorts by totalCost DESC, so the top material is the first row.
  const topMaterial = materials[0];

  return (
    <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
      <Col xs={24} sm={12} xl={8}>
        <Card
          size="small"
          style={cardStyle("#f6ffed", "#b7eb8f")}
          bodyStyle={{ padding: "12px 16px" }}
        >
          <div style={titleStyle("#389e0d")}>
            <WalletOutlined /> Total Material Cost
          </div>
          <Tooltip title={fmtINR(totalCost)}>
            <div style={valueStyle}>{fmtINRCompact(totalCost)}</div>
          </Tooltip>
          <div style={subStyle}>Across all task BOQ lines</div>
        </Card>
      </Col>
      <Col xs={24} sm={12} xl={8}>
        <Card
          size="small"
          style={cardStyle("#f0f7ff", "#bae0ff")}
          bodyStyle={{ padding: "12px 16px" }}
        >
          <div style={titleStyle("#1677ff")}>
            <AppstoreOutlined /> Distinct Materials
          </div>
          <div style={valueStyle}>{materialCount}</div>
          <div style={subStyle}>
            {materialCount === 1 ? "material" : "materials"} in scope
          </div>
        </Card>
      </Col>
      <Col xs={24} sm={12} xl={8}>
        <Card
          size="small"
          style={cardStyle("#f9f4ff", "#d3adf7")}
          bodyStyle={{ padding: "12px 16px" }}
        >
          <div style={titleStyle("#722ed1")}>
            <TrophyOutlined /> Top Material
          </div>
          {topMaterial ? (
            <>
              <Tooltip title={fmtINR(topMaterial.totalCost)}>
                <div style={valueStyle}>
                  {fmtINRCompact(topMaterial.totalCost)}
                </div>
              </Tooltip>
              <Tooltip title={topMaterial.materialName}>
                <div style={subStyle}>{topMaterial.materialName || "—"}</div>
              </Tooltip>
            </>
          ) : (
            <>
              <div style={valueStyle}>—</div>
              <div style={subStyle}>No materials yet</div>
            </>
          )}
        </Card>
      </Col>
    </Row>
  );
};

export default MaterialKpiCards;
